import React, { useContext, useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import {
  ActionButtonHandle,
  DetailContext,
  FormPanel,
  RemoteTableField,
  ScrollableSource,
  TableFieldHandle,
  TableFieldToolbarProps,
  UserContext,
} from '@eas/common-web';
import { FormTableCount } from '@components/form/table-field/form-table-count';
import { TextNote } from '@models';
import { EsFileState, Role } from '@enums';
import { useNoteColumns } from './note-columns';
import { NoteDialog } from './note-dialog';
import { NoteToolbar } from './note-toolbar';

export interface NoteFieldProps {
  disabled: boolean;
  source: ScrollableSource<TextNote>;
  label?: React.ReactNode;
}

export function NoteField({
  disabled,
  source,
  label = (
    <FormattedMessage id="ES__NOTES__PANEL_TITLE" defaultMessage="Poznámky" />
  ),
}: NoteFieldProps) {
  const columns = useNoteColumns();
  const { hasPermission, user } = useContext(UserContext);
  const { source: detailSource } = useContext(DetailContext);

  const tableRef = useRef<TableFieldHandle>(null);
  const editRef = useRef<ActionButtonHandle>(null);

  const ToolbarComponent = useMemo(
    () =>
      function Toolbar(props: TableFieldToolbarProps) {
        return <NoteToolbar {...props} ref={editRef} disabled={disabled} />;
      },
    [disabled]
  );

  if (!source) {
    return <> Missing NOTE_SOURCE </>;
  }

  return (
    <FormPanel
      label={label}
      expandable={true}
      defaultExpanded={true}
      summary={<FormTableCount source={source} />}
    >
      <RemoteTableField
        ref={tableRef}
        disabled={disabled}
        columns={columns}
        source={source}
        maxRows={7}
        ToolbarComponent={ToolbarComponent}
        onRowClick={(_, index) => {
          /** Allow Lustrum to edit notes on files in the NEW state. */
          const isLustrumOnNewFile =
            hasPermission(Role.LUSTRUM) &&
            (detailSource?.data as { state?: EsFileState } | undefined)
              ?.state === EsFileState.NEW;

          const hasPermissionToEdit =
            hasPermission(Role.ADMIN) ||
            hasPermission(Role.INDEX) ||
            isLustrumOnNewFile ||
            source.items?.[index]?.createdBy?.id === user.id;

          if (!disabled && hasPermissionToEdit) {
            tableRef.current?.setSelectedIndex(index);
            editRef.current?.executeAction();
          }
        }}
        highlightSelectedRow={true}
        showDetailBtnCond={() => true}
        FormFieldsComponent={() => (
          <div style={{ width: 700 }}>
            <Typography variant="body1" style={{ marginBottom: 8 }}>
              <FormattedMessage
                id="ES__NOTES__DESCRIPTION"
                defaultMessage="Text poznámky:"
              />
            </Typography>
            <NoteDialog />
          </div>
        )}
      />
    </FormPanel>
  );
}
